/*
 * Copyright (c) 2024. Dave van Rijn Development
 */
import React, {
    ChangeEventHandler,
    CSSProperties,
    DetailedHTMLProps,
    EventHandler,
    FocusEventHandler,
    InputHTMLAttributes,
    KeyboardEventHandler,
    MouseEventHandler,
    PureComponent,
    ReactNode,
    TextareaHTMLAttributes
} from 'react';
import {assert, enterPressed} from "../../util/controlUtil";
import SimpleInput from "../simple/v2/simpleInputV2";
import {ControlVariant, ErrorType, OrnamentShape} from "../../util/interfaces";
import {generateComponentId} from '../../../..';

type HTMLProps = Omit<InputHTMLAttributes<any>, 'onChange' | 'step' | 'children'>;

export interface DVRInputControllerProps extends HTMLProps {
    // Events
    onChange: (value: string | number, evt: React.ChangeEvent) => void;
    onEnter?: KeyboardEventHandler;
    onFocus?: FocusEventHandler;
    // Theme
    variant: ControlVariant;
    baseColor?: string;
    contrastColor?: string;
    // Classes
    errorClassName: string;
    inputContainerClassName: string;
    labelClassName: string;
    // Styles
    containerStyle?: CSSProperties;
    labelStyle?: CSSProperties;
    errorStyle?: CSSProperties;
    inputStyle?: CSSProperties;
    // Other
    error: ErrorType;
    inputProps?: InputHTMLAttributes<any> & TextareaHTMLAttributes<any>;
    ornaments?: OrnamentShape | OrnamentShape[];
    area: boolean;
    autoSelect: boolean;
    autoFocus: boolean;
    clearAble: boolean;
    fullWidth: boolean;
    defaultValue?: string | number;
    label?: string | number;
    value?: string | number;
    step?: number;
    unControlled?: boolean;
    children?: ReactNode | ReactNode[] | OrnamentShape | OrnamentShape[];
}

export interface DVRInputProps extends Omit<DVRInputControllerProps, 'value' | 'onChange'> {
    // Events
    onChange: ChangeEventHandler;
    onFocus: FocusEventHandler;
    onBlur: FocusEventHandler;
    onClear: EventHandler<any>;
    onMouseEnter: MouseEventHandler;
    onMouseLeave: MouseEventHandler;
    onKeyDown: KeyboardEventHandler;
    onClick: MouseEventHandler;
    // State
    isFocused: boolean;
    isHovered: boolean;
    // Theme
    variant: ControlVariant;
    baseColor?: string;
    contrastColor?: string;
    borderColor?: string;
    // Classes
    errorClassName: string;
    inputContainerClassName: string;
    labelClassName: string;
    // Styles
    containerStyle?: CSSProperties;
    labelStyle?: CSSProperties;
    errorStyle?: CSSProperties;
    inputStyle?: CSSProperties;
    // Other
    error: ErrorType;
    inputProps?: Omit<DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement>, HTMLInputElement | HTMLTextAreaElement>, 'crossOrigin'>;
    ornaments?: OrnamentShape | OrnamentShape[];
    clearAble: boolean;
    fullWidth: boolean;
    label?: string | number;
    value: string | number;
    step?: number;
    unControlled?: boolean;
}

export const DefaultInputProps: Partial<DVRInputProps> = {
    area: false,
    clearAble: true,
    disabled: false,
    autoFocus: false,
    autoSelect: false,
    error: null,
    errorClassName: '',
    fullWidth: true,
    className: '',
    inputContainerClassName: '',
    labelClassName: '',
    readOnly: false,
    type: 'text',
    variant: ControlVariant.SIMPLE,
}

interface State {
    isFocused: boolean;
    isHovered: boolean;
    value: string | number;
}

export default class InputControllerV2 extends PureComponent<DVRInputControllerProps, State> {
    static defaultProps: Partial<DVRInputProps> = DefaultInputProps;
    private readonly id: string;

    private getValue = (props: DVRInputControllerProps = this.props): string | number => {
        if (props.value !== undefined) return props.value;
        if (props.defaultValue !== undefined) return props.defaultValue;
        return '';
    }

    private validateProps = (props: DVRInputControllerProps) => {
        if (props.value === undefined && props.defaultValue === undefined)
            return 'value and defaultValue cannot both be undefined.';
        return null;
    };

    constructor(props: DVRInputControllerProps) {
        super(props);
        const error = this.validateProps(props);
        assert(error === null, error);
        this.id = generateComponentId(props.id);
        this.state = {
            isFocused: false,
            isHovered: false,
            value: this.getValue(props),
        }
    }

    onChange = (evt: React.ChangeEvent<any>) => {
        const {onChange, type, step} = this.props, {value} = evt.target;
        let finalValue: string | number = value;
        if (type === 'number') {
            if (step !== undefined) {
                if (Number.isInteger(step)) finalValue = parseInt(finalValue as string);
                else finalValue = parseFloat(finalValue as string);
            }
        }
        evt.persist();
        this.setState({value: finalValue.toString()}, () => {
            onChange(finalValue, evt);
        });
    };

    onClear = (evt: any) => {
        const {clearAble, onChange} = this.props;
        if (clearAble) {
            this.setState({value: ''}, () => {
                onChange('', evt);
                this.setInputFocus();
            });
        }
    }

    onFocus = (evt: React.FocusEvent<any>) => {
        const {onFocus} = this.props;
        this.setState({isFocused: true});
        if (onFocus) onFocus(evt);
    }

    onBlur = (evt: React.FocusEvent<any>) => {
        const {onBlur} = this.props;
        this.setState({isFocused: false});
        if (onBlur) onBlur(evt);
    }

    onClick = (evt: React.MouseEvent<any>) => {
        const {onClick} = this.props, {isFocused} = this.state;
        if (!isFocused) this.setInputFocus();
        if (onClick) onClick(evt);
    }

    onKeyDown = (evt: React.KeyboardEvent<any>) => {
        const {onKeyDown, onEnter} = this.props;
        if (onEnter && enterPressed(evt)) onEnter(evt);
        else if (onKeyDown) onKeyDown(evt);
    }

    onMouseEnter = (evt: React.MouseEvent<any>) => {
        const {onMouseEnter} = this.props;
        this.setState({isHovered: true});
        if (onMouseEnter) onMouseEnter(evt);
    };

    onMouseLeave = (evt: React.MouseEvent<any>) => {
        const {onMouseLeave} = this.props;
        this.setState({isHovered: false});
        if (onMouseLeave) onMouseLeave(evt);
    };

    getInput = (): HTMLInputElement | HTMLTextAreaElement | null => {
        const input = document.getElementById(`${this.id}-input`);
        if (input !== null) return (input as HTMLInputElement | HTMLTextAreaElement);
        return null;
    };

    getViewValue = (): string | number => {
        const {value, unControlled} = this.props;
        if (unControlled || value === undefined) return this.state.value;
        return value;
    };

    setInputFocus = (retryOnNull: boolean = true) => {
        const input = this.getInput();
        if (input === null && retryOnNull)
            window.setTimeout(() => {
                this.setInputFocus(false);
            });
        if (input !== null) {
            input.focus();
            this.setState({isFocused: true});
        }
    };

    componentDidMount = () => {
        if (this.props.autoFocus) this.setInputFocus();
    };

    render = () => {
        const {variant, unControlled} = this.props, {isFocused, isHovered} = this.state;
        const props = {
            ...this.props,
            onChange: this.onChange,
            onClear: this.onClear,
            onFocus: this.onFocus,
            onBlur: this.onBlur,
            onClick: this.onClick,
            onMouseEnter: this.onMouseEnter,
            onMouseLeave: this.onMouseLeave,
            onKeyDown: this.onKeyDown,
            isFocused,
            isHovered,
            value: this.getViewValue(),
            id: this.id,
            unControlled,
        }
        if (variant === ControlVariant.SIMPLE)
            return <SimpleInput {...props}>{this.props.children}</SimpleInput>
    };
}